import { NamespaceType } from '@wix/ambassador-devcenter-apps-v1-app/types';
import { useBlocksService } from './useBlocksService';
import type { useModalState } from './useBlocksNamespaceModalState';

export const useValidateAndSet = (state: ReturnType<typeof useModalState>) => {
  const {
    isAppNameExists,
    isNamespaceExists,
    getAppNameSuggestions,
    getNamespaceSuggestionsByType,
  } = useBlocksService();

  const getAppNameErrorKey = async (name: string) => {
    if (name.length < 1 || name.length > 30) {
      return 'app.template.page.blocks.namespace.modal.name.general.error';
    } else if (name.toLowerCase().includes('wix')) {
      return 'app.template.page.blocks.namespace.modal.name.trademark.violation';
    } else if (await isAppNameExists(name)) {
      return 'app.template.page.blocks.modal.same.app.name.error';
    }
    return undefined;
  };

  const getNamespaceError = async (namespace: string) => {
    const namespaceSuffix = namespace.split('/')[1];
    if (namespaceSuffix.length < 3 || namespaceSuffix.length > 100) {
      return 'app.template.page.blocks.namespace.modal.general.format.error';
    } else if (namespaceSuffix.toLowerCase().includes('wix')) {
      return 'app.template.page.blocks.namespace.modal.trademark.violation';
    } else if (await isNamespaceExists(namespace)) {
      return 'app.template.page.blocks.namespace.modal.same.name.error';
    }
    return undefined;
  };

  const validateAppName = async (name: string) => {
    state.setIsValidated(false);
    state.setIsNameValidating(true);
    const error = await getAppNameErrorKey(name);
    if (error) {
      state.setNameError(error);
    } else {
      state.setNameError(undefined);
      state.setNamespace(
        await getNamespaceSuggestionsByType(
          name,
          state.selectedNamespacePrefixType,
        ),
      );
    }
    state.setIsNameValidating(false);
  };

  const validateAppNamespace = async (newNamespace: string) => {
    state.setIsValidated(false);
    state.setIsNamespaceValidating(true);
    const error = await getNamespaceError(newNamespace);
    if (error) {
      state.setNamespaceError(error);
    } else {
      if (state.nameError === undefined) {
        state.setIsValidated(true);
      }
      state.setNamespaceError(undefined);
    }
    state.setIsNamespaceValidating(false);
  };

  const fetchSuggestions = async (name) => {
    const { newName, newNamespaces } = await getAppNameSuggestions(name);
    const { account, namespace, company } = newNamespaces;
    state.setName(newName);
    state.setNamespace(namespace);
    state.setNamespacePrefixes({ ACCOUNT: account, COMPANY: company });
    state.setSelectedNamespacePrefixType(
      company ? NamespaceType.COMPANY : NamespaceType.ACCOUNT,
    );
    state.setIsSuggestionsFetched(true);
    state.setIsValidated(true);
  };

  return {
    validateAppName,
    validateAppNamespace,
    fetchSuggestions,
  };
};
